import type {ReactElement} from 'react';
import React, {useMemo, useState} from 'react';
import {ContextualMenu} from '@Components/contextual-menu';
import {DefaultEditingOptionsHeader} from '@Components/poster-editor/components/poster-editing-side-panel/components/default-editing-options-header';
import {ItemHeaderTab} from '@Components/poster-editor/components/poster-editing-side-panel/poster-editing-side-panel.types';
import {ItemPositionContextualOptions} from '@Components/poster-editor/components/poster-editing-side-panel/components/item-position-contextual-options';
import {PosterItemControls} from '@Components/poster-editor/components/poster-editing-side-panel/components/poster-item-controls';
import {ItemOpacityOption} from '@Components/poster-editor/components/poster-editing-side-panel/components/item-opacity-option';
import {ItemInteractivitySection} from '@Components/poster-editor/components/poster-editing-side-panel/components/item-interactivity-section';
import {NumberOfTabsOption} from '@Components/poster-editor/components/poster-editing-side-panel/components/number-of-tabs-option';
import {ContextualMenuHeadingOption} from '@Components/contextual-menu/components/contextual-menu-heading-option';
import {ItemFontOption} from '@Components/poster-editor/components/poster-editing-side-panel/components/item-font-option';
import {ItemStylingOptions} from '@Components/poster-editor/components/poster-editing-side-panel/components/item-styling-options';
import {TextColorOption} from '@Components/poster-editor/components/poster-editing-side-panel/components/text-color-option';
import {TextOutlineOption} from '@Components/poster-editor/components/poster-editing-side-panel/components/text-outline-option';
import {ItemAlignmentOption} from '@Components/poster-editor/components/poster-editing-side-panel/components/item-alignment-option';
import {ItemLineHeightOption} from '@Components/poster-editor/components/poster-editing-side-panel/components/item-line-height-option';
import {ItemLetterSpacingOption} from '@Components/poster-editor/components/poster-editing-side-panel/components/item-letter-spacing-option';
import {TabItemPanel} from '@Components/poster-editor/components/poster-editing-side-panel/components/poster-editing-tabs-options/poster-editing-tabs-option.types';
import {TextOutlineColorPanel} from '@Components/poster-editor/components/poster-editing-side-panel/components/text-outline-color-panel';
import {TextFillColorPanel} from '@Components/poster-editor/components/poster-editing-side-panel/components/text-fill-color-panel';
import {TextItemFontSelector} from '@Components/poster-editor/components/poster-editing-side-panel/components/text-item-font-selector';
import {ContextualMenuHeader} from '@Components/contextual-menu/components/contextual-menu-header';
import {ContextualMenuTextHeader} from '@Components/contextual-menu/components/contextual-menu-text-header';
import {SeparatorStyleOption} from '@Components/poster-editor/components/poster-editing-side-panel/components/separator-style-option';
import {SeparatorColorPanel} from '@Components/poster-editor/components/poster-editing-side-panel/components/separator-color-panel';
import {TabsBackgroundOption} from '@Components/poster-editor/components/poster-editing-side-panel/components/tabs-background-option';
import {TabsBackgroundColorPanel} from '@Components/poster-editor/components/poster-editing-side-panel/components/tabs-background-color-panel';
import {TabsContentOption} from '@Components/poster-editor/components/poster-editing-side-panel/components/tabs-content-option';
import {ItemLockInformationBox} from '@Components/poster-editor/components/poster-editing-side-panel/components/item-lock-information-box';
import {LanguageNotSupportedByTextFontOption} from '@Components/poster-editor/components/poster-editing-side-panel/components/language-not-supported-by-text-font-option';
import {ItemAnimationOption} from '@Components/poster-editor/components/poster-editing-side-panel/components/item-animation-option/item-animation-option';
import {ItemAnimationPanel} from '@Components/poster-editor/components/poster-editing-side-panel/components/item-animation-panel/item-animation-panel';
import {ContextualMenuContentKeys} from '@Components/poster-editor/components/poster-editing-side-panel/components/contextual-options/contextual-options.types';
import {MenuPanels} from '@Components/poster-editor/components/poster-editing-side-panel/components/poster-editing-menu-options/poster-editing-menu-options.types';

export function PosterEditingTabsOptions(): ReactElement<any> {
  const [currentItemHeaderTab, setCurrentItemHeaderTab] = useState(ItemHeaderTab.EDIT);
  const [currentlyOpenedPanel, setCurrentlyOpenedPanel] = useState(TabItemPanel.DEFAULT);

  const getHeaderText = (): string => {
    if (currentItemHeaderTab === ItemHeaderTab.POSITION) {
      return window.i18next.t('pmwjs_position');
    }
    if (currentlyOpenedPanel === TabItemPanel.ANIMATION) {
      return window.i18next.t('pmwjs_animation');
    } else {
      return window.i18next.t('pmwjs_color');
    }
  };

  const getHeader = (): ReactElement<any> | null => {
    if (isSubPanelOpened() || currentItemHeaderTab === ItemHeaderTab.POSITION) {
      if (currentlyOpenedPanel === TabItemPanel.FONT) {
        return null;
      }
      return (
        <ContextualMenuHeader id="tabs-panel-header">
          <ContextualMenuTextHeader
            showBackButton
            onBackButton={(): void => {
              if (currentItemHeaderTab === ItemHeaderTab.POSITION) {
                setCurrentItemHeaderTab(ItemHeaderTab.EDIT);
              }
              setCurrentlyOpenedPanel(TabItemPanel.DEFAULT);
            }}
            text={getHeaderText()}
          />
        </ContextualMenuHeader>
      );
    }
    return null;
  };

  const getOptions = (): ReactElement<any> => {
    switch (currentItemHeaderTab) {
      case ItemHeaderTab.POSITION:
        return <ItemPositionContextualOptions />;
      case ItemHeaderTab.EDIT:
        return getEditOptions();
      default:
        throw new Error('unhandled tab item for contextual header');
    }
  };

  const getDefaultOptions = (): ReactElement<any> => {
    return (
      <>
        <ItemLockInformationBox />
        <PosterItemControls
          onClickPosition={(): void => {
            setCurrentItemHeaderTab(ItemHeaderTab.POSITION);
          }}
        />
        <ItemOpacityOption />
        <ItemAnimationOption
          onAnimationTypeClick={(): void => {
            setCurrentlyOpenedPanel(TabItemPanel.ANIMATION);
          }}
        />
        <NumberOfTabsOption />
        <ContextualMenuHeadingOption id="tabs-content-heading-option" text={window.i18next.t('pmwjs_edit_mask_text')} />
        <TabsContentOption />
        <ContextualMenuHeadingOption id="tabs-text-styles-heading-option" text={window.i18next.t('pmwjs_styles')} />
        <ItemFontOption
          onClickFontButton={(): void => {
            setCurrentlyOpenedPanel(TabItemPanel.FONT);
          }}
        />
        <LanguageNotSupportedByTextFontOption />
        <ItemStylingOptions />
        <TextColorOption
          onClickColorFill={(): void => {
            setCurrentlyOpenedPanel(TabItemPanel.FILL);
          }}
        />
        <TextOutlineOption
          onClickColorFill={(): void => {
            setCurrentlyOpenedPanel(TabItemPanel.OUTLINE);
          }}
        />
        <ItemAlignmentOption />
        <ItemLineHeightOption />
        <ItemLetterSpacingOption />
        <TabsBackgroundOption
          onClickColorFill={(): void => {
            setCurrentlyOpenedPanel(TabItemPanel.BACKGROUND_COLOR);
          }}
        />
        <ContextualMenuHeadingOption id="tabs-separator-heading-option" text={window.i18next.t('pmwjs_separator')} />
        <SeparatorStyleOption
          onClickColorFill={(): void => {
            setCurrentlyOpenedPanel(TabItemPanel.SEPARATOR);
          }}
        />
        <ItemInteractivitySection />
      </>
    );
  };

  const getSubPanelOptions = (): ReactElement<any> => {
    switch (currentlyOpenedPanel) {
      case TabItemPanel.OUTLINE:
        return <TextOutlineColorPanel />;
      case TabItemPanel.FILL:
        return <TextFillColorPanel isForLayoutItem />;
      case TabItemPanel.FONT:
        return (
          <TextItemFontSelector
            onBack={(): void => {
              setCurrentlyOpenedPanel(TabItemPanel.DEFAULT);
            }}
          />
        );
      case TabItemPanel.BACKGROUND_COLOR:
        return <TabsBackgroundColorPanel />;
      case TabItemPanel.SEPARATOR:
        return <SeparatorColorPanel />;
      case TabItemPanel.ANIMATION:
        return <ItemAnimationPanel />;
      default:
        throw new Error('unhandled case for panels in tabs item');
    }
  };

  const getEditOptions = (): ReactElement<any> => {
    if (isSubPanelOpened()) {
      return getSubPanelOptions();
    }
    return getDefaultOptions();
  };

  const isSubPanelOpened = (): boolean => {
    return currentlyOpenedPanel !== TabItemPanel.DEFAULT;
  };

  const getKey = (): string => {
    if (!isSubPanelOpened()) {
      return currentItemHeaderTab === ItemHeaderTab.POSITION ? ContextualMenuContentKeys.TABS_DEFAULT_POSITION : ContextualMenuContentKeys.TABS_DEFAULT_EDIT;
    }

    return '';
  };

  return useMemo((): ReactElement<any> => {
    return (
      <ContextualMenu
        id="tabs-contextual-options"
        contentClassName={currentlyOpenedPanel === TabItemPanel.FONT ? 'spacing-p-0' : ''}
        includeLeftRightPadding={!isSubPanelOpened()}
        header={getHeader()}
        contentKey={getKey()}
      >
        {getOptions()}
      </ContextualMenu>
    );
  }, [currentItemHeaderTab, currentlyOpenedPanel]);
}
